import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2, ChevronDown, Download, Phone, Share2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { SignaturePad } from '@/components/signature-pad';
import { Button, Checkbox, Input, Skeleton } from '@/components/ui';
import { ApiError, apiUrl, request } from '@/lib/api';
import { dirOf, intlLocale, setLocale } from '@/lib/i18n';
import { PublicShell } from './public-shell';

/* ---------------------------------------------------------------------------
   הצעת מחיר שהלקוח פותח מקישור — מסמך רשמי: פרטי העסק, פירוט עם כמויות
   ומע"מ, לוח תשלומים, תנאים קצרים (מקופלים), ואישור בחתימה: שם מלא, הסכמה
   לתנאים וחתימה באצבע. אחרי האישור — העותק החתום להורדה ולשיתוף.

   "לא עכשיו" הוא כפתור משני ולא נסתר: לקוח שלא מצליח לסרב פשוט לא עונה,
   ובעל העסק נשאר בלי תשובה.
   --------------------------------------------------------------------------- */

const viewSchema = z.object({
  business: z.object({
    name: z.string(),
    legalName: z.string(),
    taxId: z.string(),
    phone: z.string(),
    email: z.string(),
    address: z.string(),
  }),
  customer: z.object({ name: z.string(), address: z.string().nullable() }),
  quoteNumber: z.number().int(),
  createdAt: z.string(),
  validUntil: z.string(),
  notes: z.string().nullable(),
  lines: z.array(z.object({ description: z.string(), qty: z.number(), unitPrice: z.string(), amount: z.string() })),
  vatMode: z.enum(['added', 'included', 'exempt']).nullable(),
  vatRate: z.number(),
  /** סכום השורות לפני ההנחה. חסר בשרת ישן — אין הנחה. */
  linesTotal: z.string().optional(),
  discount: z
    .object({ amount: z.string(), percent: z.number().nullable(), label: z.string().nullable() })
    .nullable()
    .optional(),
  subtotal: z.string(),
  vat: z.string(),
  totalAmount: z.string(),
  payment: z
    .object({
      schedule: z.array(z.object({ label: z.string(), percent: z.number(), amount: z.string() })),
      methods: z.array(z.string()),
      notes: z.string(),
    })
    .nullable(),
  terms: z.array(z.string()),
  status: z.enum(['DRAFT', 'SENT', 'APPROVED', 'DECLINED', 'EXPIRED']),
  canRespond: z.boolean(),
  signature: z.object({ name: z.string(), signedAt: z.string() }).nullable(),
  locale: z.enum(['he', 'en']).catch('he').optional(),
});
type View = z.infer<typeof viewSchema>;

/**
 * פורמטים לפי השפה הנוכחית — נבנים ברינדור ולא ברמת המודול: הדף מחליף שפה
 * לשפת העסק בלי טעינה מחדש (ולכן גם לא formatCurrency מ-lib/utils, שנקבע פעם אחת).
 */
function makeFormats() {
  const locale = intlLocale();
  const date = new Intl.DateTimeFormat(locale, { timeZone: 'Asia/Jerusalem', day: 'numeric', month: 'long', year: 'numeric' });
  const currency = new Intl.NumberFormat(locale, { style: 'currency', currency: 'ILS', minimumFractionDigits: 2 });
  return {
    formatDate: (iso: string) => date.format(new Date(iso)),
    formatCurrency: (amount: string) => {
      const n = Number.parseFloat(amount);
      return Number.isFinite(n) ? currency.format(n) : '—';
    },
  };
}

export function QuotePage() {
  const { t } = useTranslation('public');
  const { formatDate, formatCurrency } = makeFormats();
  const { token } = useParams({ from: '/c/q/$token' });
  const qc = useQueryClient();
  const key = ['public-quote', token];
  const [signerName, setSignerName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const view = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => request(`/public/quote/${token}`, { schema: viewSchema, signal }),
    retry: false,
  });

  // הדף מוצג בשפת העסק, לא של הדפדפן.
  const locale = view.data?.locale;
  useEffect(() => {
    if (locale) setLocale(locale, { reload: false });
  }, [locale]);

  const approve = useMutation({
    mutationFn: () =>
      request(`/public/quote/${token}/approve`, {
        method: 'POST',
        body: { signerName: signerName.trim(), agreed, signature },
        schema: viewSchema,
      }),
    onSuccess: (v: View) => qc.setQueryData(key, v),
  });
  const decline = useMutation({
    mutationFn: () => request(`/public/quote/${token}/decline`, { method: 'POST', schema: viewSchema }),
    onSuccess: (v: View) => qc.setQueryData(key, v),
  });

  if (view.isLoading) {
    return (
      <PublicShell>
        <Skeleton className="h-6 w-40" />
        <Skeleton className="mt-6 h-56 w-full" />
      </PublicShell>
    );
  }
  if (view.isError || !view.data) {
    return (
      <PublicShell>
        <div className="py-10 text-center">
          <XCircle className="mx-auto size-10 text-fg-subtle" aria-hidden />
          <h1 className="mt-3 text-lg font-semibold text-fg">{t('quote.invalidTitle')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('quote.invalidBody')}</p>
        </div>
      </PublicShell>
    );
  }

  const v = view.data;
  const pdfUrl = apiUrl(`/public/quote/${token}/pdf`);
  const nameOk = signerName.trim().length >= 2;
  const ready = nameOk && agreed && !!signature;
  const businessName = v.business.legalName || v.business.name;

  const submit = () => {
    if (!ready) {
      setShowErrors(true);
      return;
    }
    approve.mutate();
  };

  return (
    <PublicShell business={businessName}>
      {/* פרטי העסק */}
      <div className="-mt-3 mb-5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-fg-muted">
        {v.business.taxId ? <span>{t('quote.taxId', { id: v.business.taxId })}</span> : null}
        {v.business.phone ? (
          <a href={`tel:${v.business.phone}`} className="inline-flex items-center gap-1 text-accent" dir="ltr">
            <Phone className="size-3" aria-hidden />
            {v.business.phone}
          </a>
        ) : null}
        {v.business.email ? (
          <a href={`mailto:${v.business.email}`} className="text-accent" dir="ltr">
            {v.business.email}
          </a>
        ) : null}
        {v.business.address ? <span>{v.business.address}</span> : null}
      </div>

      <div className="flex items-baseline justify-between gap-3">
        <h1 className="text-xl font-semibold text-fg">{t('quote.title')}</h1>
        <span className="tabular text-sm text-fg-muted">{t('quote.number', { n: v.quoteNumber })}</span>
      </div>
      <p className="mt-1 text-xs text-fg-muted">
        {t('quote.meta', { customer: v.customer.name, date: formatDate(v.createdAt), validUntil: formatDate(v.validUntil) })}
      </p>

      {/* פירוט */}
      <section className="mt-5 overflow-hidden rounded-2xl border border-border bg-surface">
        <ul className="divide-y divide-border">
          {v.lines.map((l, i) => (
            <li key={i} className="flex items-start justify-between gap-4 px-4 py-3">
              <span className="text-sm text-fg">
                {l.description}
                {l.qty > 1 ? (
                  <span className="mt-0.5 block text-xs text-fg-muted">
                    {l.qty} × {formatCurrency(l.unitPrice)}
                  </span>
                ) : null}
              </span>
              <span className="tabular shrink-0 text-sm text-fg">{formatCurrency(l.amount)}</span>
            </li>
          ))}
        </ul>
        <div className="space-y-1 border-t border-border bg-surface-sunken px-4 py-3.5">
          {v.discount ? (
            <>
              <Row label={t('quote.beforeDiscount')} value={formatCurrency(v.linesTotal ?? v.subtotal)} />
              <Row
                label={
                  (v.discount.label || t('quote.discount')) +
                  (v.discount.percent !== null ? ` (${v.discount.percent}%)` : '')
                }
                value={formatCurrency(`-${v.discount.amount}`)}
              />
            </>
          ) : null}
          {v.vatMode === 'added' ? (
            <>
              <Row label={t('quote.beforeVat')} value={formatCurrency(v.subtotal)} />
              <Row label={t('quote.vat', { rate: v.vatRate })} value={formatCurrency(v.vat)} />
            </>
          ) : null}
          <div className="flex items-center justify-between pt-1">
            <span className="font-medium text-fg">
              {v.vatMode === 'added' || v.vatMode === 'included' ? t('quote.totalInclVat') : t('quote.total')}
            </span>
            <span className="tabular text-lg font-semibold text-fg">{formatCurrency(v.totalAmount)}</span>
          </div>
          {v.vatMode === 'exempt' ? <p className="text-2xs text-fg-subtle">{t('quote.exempt')}</p> : null}
        </div>
      </section>

      {/* תנאי תשלום */}
      {v.payment ? (
        <section className="mt-4 rounded-2xl border border-border bg-surface px-4 py-3">
          <h2 className="text-sm font-medium text-fg">{t('quote.paymentTerms')}</h2>
          <ul className="mt-2 space-y-1">
            {v.payment.schedule.map((row, i) => (
              <li key={i} className="flex justify-between gap-3 text-sm">
                <span className="text-fg">
                  {row.label} <span className="text-fg-subtle">· {row.percent}%</span>
                </span>
                <span className="tabular text-fg">{formatCurrency(row.amount)}</span>
              </li>
            ))}
          </ul>
          {v.payment.methods.length ? (
            <p className="mt-2 text-xs text-fg-muted">{t('quote.paymentMethods', { methods: v.payment.methods.join(', ') })}</p>
          ) : null}
          {v.payment.notes ? <p className="mt-1 text-xs text-fg-muted">{v.payment.notes}</p> : null}
        </section>
      ) : null}

      {v.notes ? <p className="mt-4 whitespace-pre-wrap text-sm text-fg-muted">{v.notes}</p> : null}

      {/* תנאים — מקופלים: קצרים, אבל לא מסתירים את ההצעה */}
      {v.terms.length ? (
        <details className="group mt-4 rounded-2xl border border-border bg-surface px-4 py-3">
          <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium text-fg">
            {t('quote.terms', { count: v.terms.length })}
            <ChevronDown className="size-4 text-fg-subtle transition-transform group-open:rotate-180" aria-hidden />
          </summary>
          <ol dir={dirOf()} className="mt-3 list-decimal space-y-1.5 ps-5 text-xs leading-relaxed text-fg-muted">
            {v.terms.map((term, i) => (
              <li key={i}>{term}</li>
            ))}
          </ol>
        </details>
      ) : null}

      {v.status === 'APPROVED' ? (
        <SignedCopy v={v} pdfUrl={pdfUrl} businessName={businessName} />
      ) : v.status === 'DECLINED' ? (
        <Result title={t('quote.declinedTitle')} body={t('quote.declinedBody')} />
      ) : v.status === 'EXPIRED' ? (
        <Result title={t('quote.expiredTitle')} body={t('quote.expiredBody')} />
      ) : v.canRespond ? (
        <section className="mt-6 space-y-4 rounded-2xl border border-accent-border bg-accent-subtle/40 p-4">
          <h2 className="text-base font-semibold text-fg">{t('quote.approveTitle')}</h2>
          <div className="space-y-1.5">
            <label htmlFor="signer-name" className="text-sm font-medium text-fg">
              {t('quote.fullName')}
            </label>
            <Input
              id="signer-name"
              autoComplete="name"
              value={signerName}
              maxLength={100}
              invalid={showErrors && !nameOk}
              onChange={(e) => setSignerName(e.target.value)}
            />
          </div>
          <div>
            <p className="mb-1.5 text-sm font-medium text-fg">{t('quote.signature')}</p>
            <SignaturePad label={t('quote.signatureAria')} onChange={setSignature} />
            {showErrors && !signature ? <p className="text-xs text-danger">{t('quote.signatureMissing')}</p> : null}
          </div>
          <label className="flex items-start gap-2 text-sm text-fg">
            <Checkbox checked={agreed} onCheckedChange={(on) => setAgreed(on === true)} className="mt-0.5" />
            <span>
              {t('quote.agree')}{' '}
              <strong className="tabular">{formatCurrency(v.totalAmount)}</strong>.
            </span>
          </label>
          {showErrors && !agreed ? <p className="-mt-2 text-xs text-danger">{t('quote.agreeMissing')}</p> : null}
          {/* חובת יידוע בעת האיסוף: הלקוח שחותם אינו משתמש של המערכת, והוא
              צריך לדעת מה נשמר עליו לפני שהוא מניח את האצבע על המסך. */}
          <p className="text-xs text-fg-muted">
            {t('quote.consentNotice')}{' '}
            <a href="/privacy" className="text-accent hover:underline" target="_blank" rel="noreferrer">
              {t('quote.privacyPolicy')}
            </a>
          </p>
          <Button size="lg" className="h-14 w-full text-base" loading={approve.isPending} onClick={submit}>
            <CheckCircle2 aria-hidden />
            {t('quote.approve')}
          </Button>
          <div className="flex items-center justify-between">
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline">
              {t('quote.viewPdf')}
            </a>
            <Button variant="ghost" size="sm" loading={decline.isPending} onClick={() => decline.mutate()}>
              {t('quote.notNow')}
            </Button>
          </div>
        </section>
      ) : null}

      {approve.isError || decline.isError ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {isConflict(approve.error ?? decline.error)
            ? t('quote.conflict')
            : t('quote.actionFailed')}
        </p>
      ) : null}
    </PublicShell>
  );
}

/** אחרי האישור: מי חתם ומתי, והעותק החתום — הורדה, ושיתוף לוואטסאפ דרך תפריט השיתוף של הטלפון. */
function SignedCopy({ v, pdfUrl, businessName }: { v: View; pdfUrl: string; businessName: string }) {
  const { t } = useTranslation('public');
  const { formatDate } = makeFormats();
  const [sharing, setSharing] = useState(false);
  const fileName = t('quote.fileName', { n: v.quoteNumber });
  const text = t('quote.shareText', { n: v.quoteNumber, business: businessName });

  const share = async () => {
    setSharing(true);
    try {
      const blob = await fetch(pdfUrl).then((r) => r.blob());
      const file = new File([blob], fileName, { type: 'application/pdf' });
      if (navigator.canShare?.({ files: [file] })) {
        // בטלפון: נפתח תפריט השיתוף עם הקובץ — WhatsApp, מייל, שמירה.
        await navigator.share({ files: [file], title: text, text });
      } else {
        // מחשב: WhatsApp Web עם קישור לעותק.
        window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${window.location.href}`)}`, '_blank', 'noopener');
      }
    } catch {
      /* המשתמש סגר את תפריט השיתוף */
    } finally {
      setSharing(false);
    }
  };

  return (
    <section className="mt-6 space-y-3 rounded-2xl bg-success-subtle p-4">
      <p className="flex items-center gap-2 font-medium text-success">
        <CheckCircle2 className="size-5" aria-hidden />
        {t('quote.signedTitle')}
      </p>
      {v.signature ? (
        <p className="text-sm text-fg-muted">
          {t('quote.signedBy', { name: v.signature.name, date: formatDate(v.signature.signedAt), business: businessName })}
        </p>
      ) : null}
      <div className="flex flex-col gap-2 sm:flex-row">
        <a
          href={pdfUrl}
          download={fileName}
          className="inline-flex h-11 items-center justify-center gap-2 sm:flex-1 rounded-(--radius-md) bg-accent px-4 text-sm font-medium text-fg-on-accent"
        >
          <Download className="size-4" aria-hidden />
          {t('quote.download')}
        </a>
        <Button variant="secondary" className="h-11 sm:flex-1" loading={sharing} onClick={() => void share()}>
          <Share2 aria-hidden />
          {t('quote.share')}
        </Button>
      </div>
      <p className="text-2xs text-fg-subtle">{t('quote.copySent')}</p>
    </section>
  );
}

const isConflict = (e: unknown) => e instanceof ApiError && e.status === 409;

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-sm text-fg-muted">
      <span>{label}</span>
      <span className="tabular">{value}</span>
    </div>
  );
}

function Result({ title, body }: { title: string; body: string }) {
  return (
    <div className="mt-6 rounded-2xl bg-surface-sunken p-4">
      <p className="font-medium text-fg">{title}</p>
      <p className="mt-1 text-sm text-fg-muted">{body}</p>
    </div>
  );
}
